import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import PersonalityCard from '@/components/PersonalityCard';
import ConflictPollCard from '@/components/ConflictPollCard';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { GROUP_PROFILE, PERSONALITY } from '@/constants/testIds';
import { Sparkles, RefreshCw, Users, AlertCircle, CheckCircle2, MinusCircle, MessageCircle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
    green: { label: 'Aligned', icon: CheckCircle2, color: '#4A7C59', bg: 'rgba(74,124,89,0.10)' },
    amber: { label: 'Slight mismatch', icon: MinusCircle, color: '#C77B3A', bg: 'rgba(199,123,58,0.10)' },
    red:   { label: 'Conflict',  icon: AlertCircle, color: '#D05C45', bg: 'rgba(208,92,69,0.10)' },
};

export default function GroupProfileTab({ tripId, trip, refreshKey }) {
    const { user } = useAuth();
    const [state, setState] = useState({ loading: true, data: null });
    const [persState, setPersState] = useState({ loading: true, data: null });
    const [pollsState, setPollsState] = useState({ data: null });
    const [refreshing, setRefreshing] = useState(false);

    const loadProfile = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/group-profile`);
            setState({ loading: false, data });
        } catch (err) {
            setState({ loading: false, data: null });
            toast.error(err?.response?.data?.detail || 'Could not load group profile');
        }
    }, [tripId]);

    const loadPersonalities = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/personalities`);
            setPersState({ loading: false, data });
        } catch {
            setPersState({ loading: false, data: null });
        }
    }, [tripId]);

    const loadPolls = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/polls`);
            setPollsState({ data });
        } catch {
            setPollsState({ data: null });
        }
    }, [tripId]);

    const load = useCallback(async () => {
        setRefreshing(true);
        await Promise.all([loadProfile(), loadPersonalities(), loadPolls()]);
        setRefreshing(false);
    }, [loadProfile, loadPersonalities, loadPolls]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const pollsByConflictId = (pollsState.data?.polls || []).reduce((acc, p) => {
        acc[p.conflict_id] = p;
        return acc;
    }, {});

    const updatePoll = (poll) => {
        setPollsState((prev) => {
            const polls = (prev.data?.polls || []).filter((p) => p.poll_id !== poll?.poll_id);
            if (poll) polls.push(poll);
            return { data: { ...(prev.data || {}), polls } };
        });
    };

    if (state.loading || persState.loading) {
        return <div className="ns-card p-12 text-center text-[var(--ns-text2)] mt-6">Reading the room…</div>;
    }

    const { available, submitted, total, profile } = state.data || {};

    if (!available) {
        return (
            <div data-testid={GROUP_PROFILE.waiting} className="ns-card p-10 sm:p-14 text-center mt-6">
                <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--ns-soft)' }}>
                    <Users className="text-[var(--ns-clay)]" />
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight">Group profile coming soon</h3>
                <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto leading-relaxed">
                    Once at least 2 travelers have submitted preferences, NextStop will analyse where you align — and where you might want to chat. So far: <strong>{submitted}/{total}</strong>.
                </p>
            </div>
        );
    }

    const personalities = persState.data?.personalities || [];
    const groupInsight = persState.data?.group_insight || '';

    return (
        <div data-testid={GROUP_PROFILE.container} className="mt-6 space-y-8">
            {/* Top summary */}
            <div className="ns-card p-6 sm:p-8">
                <div className="flex items-start gap-4 flex-wrap">
                    <span className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                        <Sparkles className="text-[var(--ns-clay)]" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Your group&apos;s travel style</div>
                        <p data-testid={GROUP_PROFILE.summary} className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-2 leading-snug text-[var(--ns-text)]">
                            {profile.summary || 'Your group is just getting started — submit more preferences for richer insights.'}
                        </p>
                        <div className="text-xs text-[var(--ns-text3)] mt-3">Based on {submitted}/{total} submissions</div>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={load}
                        disabled={refreshing}
                        data-testid={GROUP_PROFILE.refreshButton}
                        className="rounded-full h-10 text-[var(--ns-text2)]"
                    >
                        <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Travel personalities */}
            {personalities.length > 0 && (
                <div data-testid={PERSONALITY.section}>
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Travel personalities</div>
                            <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">Meet the crew</h3>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {personalities.map((p) => (
                            <PersonalityCard key={p.user_id} personality={p} />
                        ))}
                    </div>

                    {groupInsight && (
                        <div data-testid={PERSONALITY.groupInsight} className="ns-card mt-6 p-6 sm:p-7 flex items-start gap-4" style={{ background: 'linear-gradient(135deg, rgba(29,45,68,0.06), white)' }}>
                            <span className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-navy)' }}>
                                <Wand2 size={16} className="text-white" />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Group dynamic</div>
                                <p className="font-display text-lg sm:text-xl font-semibold tracking-tight mt-1 leading-snug text-[var(--ns-text)]">{groupInsight}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Alignment grid */}
            {profile.alignment?.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-3">Alignment by category</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {profile.alignment.map((a, idx) => {
                            const meta = STATUS_META[a.status] || STATUS_META.amber;
                            const Icon = meta.icon;
                            return (
                                <div
                                    key={`${a.category}-${idx}`}
                                    data-testid={GROUP_PROFILE.alignmentItem}
                                    className="ns-card p-5 flex items-start gap-3"
                                    style={{ borderLeftWidth: 4, borderLeftColor: meta.color }}
                                >
                                    <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: meta.bg }}>
                                        <Icon size={16} style={{ color: meta.color }} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-display font-semibold text-[var(--ns-text)] capitalize">{a.category}</div>
                                            <span
                                                className="text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full"
                                                style={{ background: meta.bg, color: meta.color }}
                                            >{meta.label}</span>
                                        </div>
                                        <p className="text-sm text-[var(--ns-text2)] mt-1.5 leading-relaxed">{a.message}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Conflict callouts */}
            <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-3">Discussion prompts</div>
                {profile.conflicts?.length > 0 ? (
                    <div className="space-y-3">
                        {profile.conflicts.map((c, idx) => {
                            const isRed = c.severity === 'red';
                            const color = isRed ? '#D05C45' : '#C77B3A';
                            const bgChip = isRed ? 'rgba(208,92,69,0.12)' : 'rgba(199,123,58,0.12)';
                            const bgTint = isRed
                                ? 'linear-gradient(135deg, rgba(208,92,69,0.06), white)'
                                : 'linear-gradient(135deg, rgba(199,123,58,0.05), white)';
                            const Icon = isRed ? AlertCircle : MessageCircle;
                            return (
                                <div
                                    key={idx}
                                    data-testid={GROUP_PROFILE.conflictItem}
                                    data-severity={c.severity || 'amber'}
                                    className="ns-card p-5 sm:p-6 flex items-start gap-3"
                                    style={{ background: bgTint, borderLeftWidth: 4, borderLeftColor: color }}
                                >
                                    <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bgChip }}>
                                        <Icon size={16} style={{ color }} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-display font-semibold text-[var(--ns-text)]">{c.title}</div>
                                            <span
                                                className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                                                style={{ background: bgChip, color }}
                                            >
                                                {isRed ? 'Needs alignment' : 'Worth a chat'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-[var(--ns-text2)] mt-1.5 leading-relaxed">{c.message}</p>
                                        {trip && c.conflict_id && (
                                            <ConflictPollCard
                                                trip={trip}
                                                conflict={c}
                                                poll={pollsByConflictId[c.conflict_id] || null}
                                                currentUserId={user?.user_id}
                                                onChange={updatePoll}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="ns-card p-6 text-[var(--ns-text2)] leading-relaxed flex items-start gap-3">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(74,124,89,0.10)' }}>
                            <CheckCircle2 size={16} style={{ color: '#4A7C59' }} />
                        </span>
                        <div>Your group seems well aligned.</div>
                    </div>
                )}
            </div>
        </div>
    );
}
